import type { NavigateFunction } from 'react-router-dom';

// Page transitions between the room (Homepage) and the split-screen pages.
//
// - From the Homepage (no page open): navigate; the page slides in from the right and the
//   room moves aside (.main-content's own fadeIn animation).
// - From a page to the Homepage: the page slides out (fade-out, 1s), then navigate.
// - From one page to another: the split screen stays put. The page content only fades out
//   and the new one fades in (swap-out / page-swap, see index.scss).

const SLIDE_MS = 1000; // fadeOut in index.scss
const SWAP_MS = 350;   // pageSwapOut / pageSwapIn in index.scss

const pathnameOf = (path: string) => path.split(/[?#]/)[0] || '/';

export function transitionTo(navigate: NavigateFunction, path: string) {
  const page = document.querySelector('.main-content');

  // Homepage: nothing open yet, the new page slides in by itself.
  if (!page) {
    navigate(path);
    return;
  }

  // Back to the room: slide the page out first.
  if (pathnameOf(path) === '/') {
    page.classList.add('fade-out');
    setTimeout(() => navigate(path), SLIDE_MS);
    return;
  }

  // Page to page: fade the content out, swap, fade the new content in.
  page.classList.add('swap-out');
  setTimeout(() => {
    document.body.classList.add('page-swap'); // the new page fades in instead of sliding
    navigate(path);
    // Same page component (e.g. only the query changed): it stays mounted, so fade it back in.
    requestAnimationFrame(() => page.classList.remove('swap-out'));
    setTimeout(() => {
      // Keep the fade animation on the new page ("swapped"): going back to the default
      // animation would replay the slide-in.
      document.querySelector('.main-content')?.classList.add('swapped');
      document.body.classList.remove('page-swap');
    }, SWAP_MS + 50);
  }, SWAP_MS);
}
